// src/pages/UserListPage.tsx
import BaseTable from "@components/BaseTable/BaseTable";
import CustomButton from "@components/common/Button";
import UserPermissionsService from "@/services/APIs/UserPermissionsService";
import { gridName } from "@/utils/constant/gridName";
import navigationRoutes from "@/utils/navigationRoutes";
import { UserPermissions } from "@/utils/types/userPermissions";
import { ProColumns } from "@ant-design/pro-components";
import { Popconfirm } from "antd";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { Edit2, Plus, Trash } from "lucide-react";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const UserPermissionsListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const urlPage = parseInt(searchParams.get("page") ?? "1", 10);
  const urlPageSize = parseInt(searchParams.get("pageSize") ?? "10", 10);
  const [page, setPage] = useState(urlPage);
  const [pageSize, setPageSize] = useState(urlPageSize);

  const {
    data,
    isLoading,
    refetch: refetchUserPermissions,
  } = useQuery({
    queryKey: ["userPermissions", page, pageSize],
    queryFn: () =>
      UserPermissionsService.getAllUserPermissions({ page, pageSize }),
    enabled: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 60_000,
  });

  const handleEdit = (id: string) => {
    navigate(navigationRoutes.USER_PERMISSIONS_EDIT(id));
  };

  const handleDelete = async (id: string) => {
    await UserPermissionsService.deleteUserPermissions(id);
    queryClient.invalidateQueries({ queryKey: ["userPermissions"] });
    refetchUserPermissions();
  };

  const columns: ProColumns<UserPermissions>[] = [
    {
      title: "User ID",
      render: (_, record) => `${record.user_label}`,
      sorter: (a, b) => a.user_label!.localeCompare(b.user_label!),
    },
    { title: "Allow Schema", dataIndex: "allow_schema_name" },
    { title: "For Value", dataIndex: "for_value_label" },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="edit"
          onClick={() => handleEdit(record.id ?? "")}
          icon={<Edit2 size={16} />}
          type="text"
          title="Edit User Permissions"
        />,
        <Popconfirm
          key="delete"
          title="Are you sure you want to delete this user permissions?"
          onConfirm={() => handleDelete(record.id ?? "")}
          okText="Yes"
          cancelText="No"
        >
          <CustomButton
            danger
            icon={<Trash size={16} />}
            type="text"
            title="Delete User Permissions"
          />
        </Popconfirm>,
      ],
    },
  ];

  const handleCreate = () => {
    navigate(navigationRoutes.USER_PERMISSIONS_ADD);
  };

  const toolBarRender = () => {
    return [
      <CustomButton
        key="add"
        type="primary"
        onClick={handleCreate}
        title="Add User Permissions"
      >
        <Plus size={16} className="mr-1" /> {t("Add User Permissions")}
      </CustomButton>,
    ];
  };

  return (
    <BaseTable<UserPermissions>
      loading={isLoading}
      columns={columns}
      dataSource={data?.results ?? []}
      page={page}
      pageSize={pageSize}
      total={data?.count}
      onPageChange={(page, pageSize) => {
        setPage(page);
        setPageSize(pageSize ?? 10);
        searchParams.set("page", page.toString());
        searchParams.set("pageSize", pageSize.toString());
        navigate(
          `${navigationRoutes.USER_PERMISSIONS}?${searchParams.toString()}`
        );
        refetchUserPermissions();
      }}
      rowKey="id"
      headerTitle="User Permissions List"
      toolBarRender={toolBarRender}
      request={() => refetchUserPermissions()}
      tableName={gridName.userPermissionsTable}
    />
  );
};

export default UserPermissionsListPage;
